import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, ArrowRight, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { generateBlog, getAdminToken } from "@/lib/api";

const PROMPT_EXAMPLES_EN = [
  "Best Endurance Riding Apps in 2026",
  "Equilab vs Equestic SaddleClip — head to head",
  "How to digitise a 30-horse boarding barn",
  "Best apps for foaling season",
  "AI in equestrian sport — what's actually useful in 2026?",
];

const PROMPT_EXAMPLES_NL = [
  "Beste apps voor endurance-ruiters in 2026",
  "Equilab vs Equestic SaddleClip — head-to-head",
  "Hoe digitaliseer je een pensionstal met 30 paarden",
  "Beste apps voor het veulenseizoen",
  "AI in de paardensport — wat heeft echt zin in 2026?",
];

const BlogGeneratePage = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [prompt, setPrompt] = useState("");
  const [language, setLanguage] = useState(lang);
  const [status, setStatus] = useState("idle"); // idle | generating | done | error
  const [error, setError] = useState("");

  const isNl = lang === "nl";
  const examples = isNl ? PROMPT_EXAMPLES_NL : PROMPT_EXAMPLES_EN;

  useEffect(() => {
    if (!getAdminToken()) navigate("/admin/login");
  }, [navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!prompt.trim() || status === "generating") return;
    setStatus("generating");
    setError("");
    try {
      const article = await generateBlog({ prompt: prompt.trim(), language, publish: true });
      setStatus("done");
      navigate(`/blog/${article.slug}`);
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || err.message || "Unknown error");
    }
  };

  return (
    <main>
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-12 md:py-20 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-6">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-[#2563EB]">Blog</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">
              {isNl ? "Nieuw artikel" : "Generate article"}
            </span>
          </nav>
          <div className="overline mb-3 text-[#2563EB]">
            <Sparkles size={12} className="inline mr-2" /> AI Article Generator
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-4">
            {isNl ? "Genereer een nieuw artikel" : "Generate a new article"}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">
            {isNl
              ? "Voer een onderwerp in en onze AI-engine schrijft een volledig SEO-geoptimaliseerd artikel op basis van onze app-database. Direct gepubliceerd op /blog en automatisch in de sitemap."
              : "Enter a topic and our AI engine will write a full SEO-optimised article based on our app database. Published instantly at /blog and added to the sitemap."}
          </p>
        </div>
      </section>

      <section className="hac-container py-12 max-w-3xl">
        <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-10">
          <label className="overline text-[#64748B] block mb-2">
            {isNl ? "Onderwerp" : "Topic"}
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            placeholder={isNl ? "bv. Beste apps voor endurance-ruiters in 2026" : "e.g. Best endurance riding apps in 2026"}
            className="hac-input resize-none"
            data-testid="blog-prompt"
            disabled={status === "generating"}
          />

          <div className="mt-4">
            <label className="overline text-[#64748B] block mb-2">
              {isNl ? "Taal" : "Language"}
            </label>
            <div className="flex gap-2">
              {["en", "nl"].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLanguage(l)}
                  className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                    language === l
                      ? "bg-[#2563EB] text-white border-[#2563EB]"
                      : "bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]"
                  }`}
                  data-testid={`blog-lang-${l}`}
                >
                  {l === "en" ? "🇬🇧 English" : "🇳🇱 Nederlands"}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="overline text-[#64748B] mb-3">
              {isNl ? "Voorbeelden" : "Try a starter"}
            </div>
            <div className="flex flex-wrap gap-2">
              {examples.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setPrompt(ex)}
                  className="hac-chip hover:bg-[#EFF6FF] hover:text-[#2563EB] cursor-pointer"
                  data-testid={`blog-example-${ex.slice(0, 20)}`}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-[#94A3B8] max-w-md">
              {isNl
                ? "Generatie duurt 20–40 seconden. Het artikel wordt direct gepubliceerd, je kunt het later verwijderen."
                : "Generation takes 20–40 seconds. The article is published immediately and can be deleted later."}
            </p>
            <button
              type="submit"
              disabled={!prompt.trim() || status === "generating"}
              className="hac-btn-primary disabled:opacity-50"
              data-testid="blog-generate-button"
            >
              <Sparkles size={14} />
              {status === "generating"
                ? isNl
                  ? "Genereren…"
                  : "Generating…"
                : isNl
                ? "Genereer & publiceer"
                : "Generate & publish"}
              <ArrowRight size={14} />
            </button>
          </div>

          {status === "error" && (
            <div className="mt-5 p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-sm text-[#B91C1C]" data-testid="blog-error">
              {error}
            </div>
          )}
        </form>

        <div className="mt-6 text-xs text-[#94A3B8] leading-relaxed">
          {isNl
            ? "Tip: hoe specifieker je prompt, hoe gerichter het artikel. Vermeld doelgroep, regio of een specifieke vraag."
            : "Tip: the more specific your prompt, the sharper the article. Mention persona, region or a specific question."}
        </div>
      </section>
    </main>
  );
};

export default BlogGeneratePage;
